/**
 * User dashboard with tabbed navigation
 * Consolidates Profile, Orders, and Downloads into unified interface
 * Follows the unified UX pattern with Lucide icons and skeleton loaders
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getUserDataCache, getOrdersFromDB, getOwnedProductsFromDB, OwnedProduct } from '../services/indexedDB';
import { syncUserData } from '../services/userDataSync';
import { Order } from '../types';
import UserOverview from '../components/user/UserOverview';
import UserOrders from '../components/user/UserOrders';
import UserProducts from '../components/user/UserProducts';
import { User, ClipboardList, Package, RefreshCw } from 'lucide-react';

type UserTab = 'overview' | 'orders' | 'products';

interface TabConfig {
  id: UserTab;
  label: string;
  icon: typeof User;
}

const tabs: TabConfig[] = [
  { id: 'overview', label: 'Overview', icon: User },
  { id: 'orders', label: 'Orders', icon: ClipboardList },
  { id: 'products', label: 'Products', icon: Package },
];

export default function UserPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  // User data state
  const [profile, setProfile] = useState<any>(null);
  const [statistics, setStatistics] = useState<any>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [products, setProducts] = useState<OwnedProduct[]>([]);

  // Active tab from URL or default to overview
  const [activeTab, setActiveTab] = useState<UserTab>(() => {
    const requestedTab = searchParams.get('tab') as UserTab;
    return requestedTab && tabs.some((tab) => tab.id === requestedTab)
      ? requestedTab
      : 'overview';
  });

  // Load all user data on mount
  useEffect(() => {
    loadUserData();
  }, []);

  // Update active tab when URL changes
  useEffect(() => {
    const requestedTab = searchParams.get('tab') as UserTab;
    if (requestedTab && tabs.some((tab) => tab.id === requestedTab)) {
      setActiveTab(requestedTab);
    }
  }, [searchParams]);

  async function loadUserData() {
    try {
      // Load from IndexedDB instantly
      const [cachedData, cachedOrders, cachedProducts] = await Promise.all([
        getUserDataCache(),
        getOrdersFromDB(),
        getOwnedProductsFromDB(),
      ]);

      if (cachedData) {
        setProfile(cachedData.profile);
        setStatistics(cachedData.statistics);
      }
      setOrders(cachedOrders);
      setProducts(cachedProducts);
      setLoading(false);

      // Sync in background to check for updates
      setSyncing(true);
      const updated = await syncUserData();
      if (updated) {
        // Reload from IndexedDB if data was updated
        const [freshData, freshOrders, freshProducts] = await Promise.all([
          getUserDataCache(),
          getOrdersFromDB(),
          getOwnedProductsFromDB(),
        ]);

        if (freshData) {
          setProfile(freshData.profile);
          setStatistics(freshData.statistics);
        }
        setOrders(freshOrders);
        setProducts(freshProducts);
      }
    } catch (error) {
      console.error('Failed to load user data:', error);
    } finally {
      setSyncing(false);
      setLoading(false);
    }
  }

  const handleTabChange = (tab: UserTab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="container-custom py-8">
        {/* Header skeleton */}
        <div className="mb-8">
          <div className="h-10 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2" />
          <div className="h-5 w-72 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>

        {/* Tab navigation skeleton */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-8">
          <div className="flex space-x-8">
            {tabs.map((tab) => (
              <div key={tab.id} className="py-4 px-1">
                <div className="h-5 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              </div>
            ))}
          </div>
        </div>

        {/* Content skeleton */}
        <div className="space-y-6">
          {/* Stats skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="card p-6 animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                  <div className="flex-1">
                    <div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
                    <div className="h-6 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Cards skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="card p-6 animate-pulse">
                <div className="h-5 w-24 bg-gray-200 dark:bg-gray-700 rounded mb-3" />
                <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded mb-2" />
                <div className="h-4 w-2/3 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
                <div className="h-10 w-full bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-custom py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100">
            My Account
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage your profile, orders, and downloads
          </p>
        </div>
        {syncing && (
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-full">
            <RefreshCw size={14} className="animate-spin" />
            Syncing...
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-8">
        <nav className="flex space-x-1 overflow-x-auto" aria-label="User account tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`
                  inline-flex items-center gap-2 py-4 px-4 border-b-2 font-medium text-sm whitespace-nowrap
                  transition-colors duration-200
                  ${
                    isActive
                      ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }
                `}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon size={18} />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="animate-fade-in">
        {activeTab === 'overview' && (
          <UserOverview profile={profile} statistics={statistics} syncing={syncing} />
        )}
        {activeTab === 'orders' && (
          <UserOrders orders={orders} syncing={syncing} />
        )}
        {activeTab === 'products' && (
          <UserProducts
            products={products}
            syncing={syncing}
            onDownloadComplete={loadUserData}
          />
        )}
      </div>
    </div>
  );
}
